import { useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import {
  useMapScene,
  useOpenOrders,
  useOrderPalletLps,
  useTaskDetail,
  useWorkerPositions,
} from "@/lib/api/map3d";
import { statusLabel } from "@/lib/labels";
import { buildLayout, type Layout, type Point } from "./geometry";
import { Scene3D, type FpvStatus, type RouteOverlay, type SceneHandle, type Selection } from "./Scene3D";
import { CellCard, PalletCard, WorkerCard, stepLabel } from "./DetailCard";
import {
  metres,
  minutesLabel,
  ordersByLp,
  resolveWorkers,
  sceneItems,
  STALE_MINUTES,
  taskRoute,
  zonePallets,
} from "./model";
import "./map3d.css";

/**
 * 3D xarita.
 *
 * «Admin panelga 3D xarita qo'shish va marshrutlarni 3D xarita orqali
 * hisoblash»: butun ombor — har bir haqiqiy katak o'z joyida (geometriya
 * koddan va `warehouse.json` dan), xodimlar va karalar oxirgi amali
 * joyida, hududdagi palletlar kimning buyurtmasi ekani bilan. Xodim
 * tanlansa — qolgan yo'li (yo'laklar va o'tish stellajlari bo'yicha
 * eng qisqa), katak tanlansa — Nazoratdan unga yo'l.
 */

type Tab = "workers" | "pallets" | "stats";
type Focus = "A" | "B" | "C" | "ALL";

const FOCUS_LABEL: Record<Focus, string> = { A: "A ombor", B: "B ombor", C: "C ombor", ALL: "Hammasi" };

export function Map3D() {
  const session = useSession();
  const canSeeWorkers = session.can(PERM.taskAssign);
  const scene = useMapScene();
  const positions = useWorkerPositions(canSeeWorkers);
  const orderLps = useOrderPalletLps();
  const orders = useOpenOrders();
  const sceneRef = useRef<SceneHandle>(null);

  const [selection, setSelection] = useState<Selection | null>(null);
  const [tab, setTab] = useState<Tab>("workers");
  const [focus, setFocus] = useState<Focus>("A");
  const [layers, setLayers] = useState({ workers: true, pallets: true, route: true });
  const [now, setNow] = useState(() => Date.now());
  const [fpv, setFpv] = useState<FpvStatus>({ active: false, locked: false, forklift: false });
  // Sensorli qurilmada FPV yo'q: WASD va sichqoncha qulfi u yerda ishlamaydi.
  const [coarse] = useState(() => {
    try {
      return window.matchMedia("(pointer: coarse)").matches && !window.matchMedia("(pointer: fine)").matches;
    } catch {
      return false;
    }
  });

  // Eskirish soati: belgilar o'z-o'zidan xiralashsin.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  const cells = scene.data?.cells;
  const codesKey = useMemo(() => (cells ?? []).map((cell) => cell.code).join("|"), [cells]);
  // Joylashuv faqat kodlar to'plami o'zgarganda qayta quriladi —
  // bandlik har daqiqa yangilanadi, geometriya esa yo'q.
  const layout = useMemo(() => buildLayout(codesKey ? codesKey.split("|") : []), [codesKey]);
  const items = useMemo(() => sceneItems(cells ?? [], layout), [cells, layout]);
  const cellById = useMemo(() => new Map((cells ?? []).map((cell) => [cell.location_id, cell])), [cells]);
  const codeById = useMemo(() => new Map((cells ?? []).map((cell) => [cell.location_id, cell.code])), [cells]);

  const byLp = useMemo(() => ordersByLp(orders.data?.items ?? []), [orders.data]);
  const pallets = useMemo(
    () => zonePallets(cells ?? [], layout, orderLps.items, byLp),
    [cells, layout, orderLps.items, byLp],
  );
  const workers = useMemo(
    () => resolveWorkers(positions.data?.items ?? [], layout, codeById, now),
    [positions.data, layout, codeById, now],
  );

  const selectedWorker = selection?.type === "worker" ? workers.find((w) => w.position.user_id === selection.id) : undefined;
  const selectedCell = selection?.type === "cell" ? cellById.get(selection.locationId) : undefined;
  const selectedPallet = selection?.type === "pallet" ? pallets.find((p) => p.marker.key === selection.key) : undefined;
  const task = useTaskDetail(selectedWorker?.position.current?.id);

  // --- Marshrut: xodim → qolgan satrlar → maqsad; yoki Nazorat → katak ---
  const routing = useMemo((): { overlay: RouteOverlay | null; length: number | null } => {
    if (selectedWorker) {
      const plan = taskRoute(task.data, layout, codeById);
      const start: Point = selectedWorker.marker?.point ?? layout.depot();
      const stops = [...plan.stops.map((stop) => stop.point), ...(plan.destination ? [plan.destination.point] : [])];
      if (stops.length === 0) return { overlay: null, length: null };
      const result = layout.routeThrough([start, ...stops]);
      if (!result) return { overlay: null, length: null };
      return {
        overlay: {
          points: result.points,
          stops: [
            ...plan.stops.map((stop) => ({ point: stop.point, label: stop.label })),
            ...(plan.destination ? [plan.destination] : []),
          ],
        },
        length: result.length,
      };
    }
    const placed = selectedCell ? layout.locate(selectedCell.code) : selectedPallet?.marker.placed;
    if (placed && placed.walkable) {
      const result = layout.route(layout.depot(), placed.access);
      if (result) {
        return {
          // Katak yozuvi tanlov belgisida bor — to'xtash yozuvi takrorlamaydi.
          overlay: { points: result.points, stops: [], dashed: true },
          length: result.length,
        };
      }
    }
    return { overlay: null, length: null };
  }, [selectedWorker, selectedCell, selectedPallet, task.data, layout, codeById]);

  /**
   * FPV ga kirish: tanlangan xodim/katak/pallet oldida (ularga qarab),
   * aks holda Nazorat oldida, zal ichkarisiga (shimolga) qarab.
   */
  const toggleFpv = () => {
    // Tugmadan fokus olinadi: aks holda Space (yuqoriga) tugmani qayta bosardi.
    (document.activeElement as HTMLElement | null)?.blur?.();
    if (fpv.active) {
      sceneRef.current?.exitFpv();
      return;
    }
    let spawn: Point = layout.depot();
    let yaw = 0;
    const placed = selectedCell ? layout.locate(selectedCell.code) : selectedPallet?.marker.placed;
    if (selectedWorker?.marker) {
      // Xodim (kara) o'zi ko'rinsin: yo'lak bo'ylab 8 m nariroqda turib
      // unga qaraymiz; qaysi tomon ochiq bo'lsa o'sha.
      const at = selectedWorker.marker.point;
      const side = [8000, -8000, 5000, -5000].map((dx) => ({ x: at.x + dx, z: at.z })).find((p) => layout.isFree(p));
      spawn = side ?? at;
      yaw = side ? faceYaw(side, at) : 0;
    } else if (placed) {
      spawn = placed.access;
      yaw = faceYaw(placed.access, placed);
    }
    sceneRef.current?.enterFpv(spawn, yaw);
  };
  const toggleRef = useRef(toggleFpv);
  toggleRef.current = toggleFpv;
  useEffect(() => {
    if (coarse) return;
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)) return;
      if (event.code === "KeyF" && !event.repeat && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        toggleRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [coarse]);

  const workerMarkers = useMemo(
    () => (layers.workers ? workers.flatMap((w) => (w.marker ? [w.marker] : [])) : []),
    [workers, layers.workers],
  );
  const palletMarkers = useMemo(() => (layers.pallets ? pallets.map((p) => p.marker) : []), [pallets, layers.pallets]);
  // Ro'yxatda — A zalidagi hududlar (nazorat, qabul, qaytarish…): B/C
  // binolaridagi saqlash palletlari xaritada chiziladi, lekin bu
  // ro'yxat «kimning zakasi» savoli uchun. Buyurtmaga bog'langanlari oldinda.
  const palletChips = useMemo(
    () => pallets
      .filter((p) => !inBuilding(layout, p.marker.placed.zone))
      .sort((a, b) => Number(Boolean(b.order)) - Number(Boolean(a.order)) || a.cell.code.localeCompare(b.cell.code)),
    [pallets, layout],
  );

  // Xodim tanlanganda kamera BUTUN qolgan yo'lni ko'rsatadi (topshiriq
  // yuklangach bir marta) — keyingi 5 soniyalik yangilanishlar kamerani
  // qayta siltamaydi.
  const framed = useRef<string | null>(null);
  useEffect(() => {
    if (selection?.type !== "worker") {
      framed.current = null;
      return;
    }
    if (framed.current === selection.id || !routing.overlay) return;
    framed.current = selection.id;
    sceneRef.current?.focusPoints(routing.overlay.points);
  }, [selection, routing.overlay]);

  const focusOn = (target: Focus) => {
    setFocus(target);
    const zone = target === "A" ? null : target === "ALL" ? null : layout.zoneFor(target);
    if (target === "A") sceneRef.current?.focusRect(layout.config.hall);
    else if (target === "ALL") {
      const rects = [layout.config.hall, ...layout.config.zones.map((z) => z.rect)];
      const x0 = Math.min(...rects.map((r) => r.x));
      const z0 = Math.min(...rects.map((r) => r.z));
      const x1 = Math.max(...rects.map((r) => r.x + r.w));
      const z1 = Math.max(...rects.map((r) => r.z + r.d));
      sceneRef.current?.focusRect({ x: x0, z: z0, w: x1 - x0, d: z1 - z0 });
    } else if (zone) sceneRef.current?.focusRect(zone.rect);
  };

  const selectWorker = (id: string) => {
    setSelection({ type: "worker", id });
    const worker = workers.find((w) => w.position.user_id === id);
    if (worker?.marker) sceneRef.current?.focusPoint(worker.marker.point);
  };
  const selectPallet = (key: string) => {
    setSelection({ type: "pallet", key });
    const pallet = pallets.find((p) => p.marker.key === key);
    if (pallet) sceneRef.current?.focusPoint({ x: pallet.marker.placed.x, z: pallet.marker.placed.z });
  };

  // --- Statistika ---
  const stats = useMemo(() => {
    const rack = items.filter((item) => item.placed.kind === "RACK");
    const occupied = rack.filter((item) => item.visual === "full" || item.visual === "partial").length;
    const blocked = rack.filter((item) => item.visual === "blocked").length;
    const passBays = layout.rows.reduce((sum, row) => sum + row.bays.filter((bay) => bay.pass).length, 0);
    return {
      cells: rack.length,
      occupied,
      blocked,
      passBays,
      rows: layout.rows.filter((row) => row.bays.length > 0).length,
      unplaced: layout.unplaced.length,
      orderPallets: pallets.filter((p) => p.order).length,
      zonePallets: pallets.filter((p) => !inBuilding(layout, p.marker.placed.zone)).length,
      live: workers.filter((w) => w.status === "live").length,
      forklifts: workers.filter((w) => w.position.kind === "forklift").length,
    };
  }, [items, layout, pallets, workers]);

  const loading = scene.isLoading;
  const truncated = scene.data?.truncated;

  return (
    <div className="m3d">
      <Scene3D
        ref={sceneRef}
        layout={layout}
        cells={items}
        pallets={palletMarkers}
        workers={workerMarkers}
        route={layers.route ? routing.overlay : null}
        selection={selection}
        onSelect={setSelection}
        dim={selection?.type === "worker" && Boolean(routing.overlay) && layers.route && !fpv.active}
        onFpvChange={setFpv}
      />

      {/* --- FPV: nishon va ko'rsatma --- */}
      {fpv.active && (
        <>
          <div className="m3d-crosshair" aria-hidden="true" />
          <div className="m3d-fpv-hint m3d-brackets" role="status">
            <b>FPV{fpv.forklift ? " · KARA" : ""}</b>
            <span>WASD — yurish, sichqoncha — qarash, Shift — tez, Esc — chiqish</span>
            <span className="m3d-muted">
              {fpv.locked ? "E yoki bosish — tanlash" : "Bosing — sichqonchani ushlash (yoki sudrab qarang); E — tanlash"}
              {" · Space/C — yuqori/past · H — "}
              {fpv.forklift ? "odam" : "kara"} balandligi
            </span>
          </div>
        </>
      )}

      {/* --- Yuqori panel --- */}
      <div className="m3d-top">
        <div className="m3d-title m3d-brackets">
          <span className="m3d-title-mark" aria-hidden="true">▣</span>
          <div>
            <h1>3D xarita</h1>
            <div className="m3d-kicker">
              {loading ? "Yuklanmoqda…" : `${stats.cells.toLocaleString("ru-RU")} katak · ${stats.rows} qator · ${stats.passBays} o'tish`}
            </div>
          </div>
        </div>
        <div className="m3d-seg" role="tablist" aria-label="Ombor">
          {(Object.keys(FOCUS_LABEL) as Focus[]).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={focus === key}
              className={focus === key ? "active" : ""}
              onClick={() => focusOn(key)}
            >
              {FOCUS_LABEL[key]}
            </button>
          ))}
        </div>
        <div className="m3d-seg m3d-seg-layers" aria-label="Qatlamlar">
          {([
            ["workers", "Xodimlar"],
            ["pallets", "Palletlar"],
            ["route", "Marshrut"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={layers[key]}
              className={layers[key] ? "active" : ""}
              onClick={() => setLayers((value) => ({ ...value, [key]: !value[key] }))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {scene.isError && (
        <div className="m3d-banner m3d-brackets">Kataklar o'qilmadi: {String((scene.error as Error)?.message ?? scene.error)}</div>
      )}
      {truncated && <div className="m3d-banner m3d-brackets">Reyestr juda katta — xarita qisman chizildi.</div>}

      {/* --- Chap karta --- */}
      <div className="m3d-left">
        {selectedWorker && (
          <WorkerCard
            info={selectedWorker}
            task={task.data}
            codeById={codeById}
            routeLength={routing.length}
            onClose={() => setSelection(null)}
          />
        )}
        {selectedCell && layout.locate(selectedCell.code) && (
          <CellCard
            cell={selectedCell}
            placed={layout.locate(selectedCell.code)!}
            routeLength={routing.length}
            byLp={byLp}
            onClose={() => setSelection(null)}
          />
        )}
        {selectedPallet && <PalletCard info={selectedPallet} onClose={() => setSelection(null)} />}
        {!selection && !fpv.active && (
          <section className="m3d-card m3d-brackets m3d-hint">
            <div className="m3d-kicker">Ko'rsatma</div>
            <p>Katak, pallet yoki xodimni bosing — holati shu yerda.</p>
            <p>Sariq X — o'tish stellaji (faqat 5–6 qavat): ostidan qo'shni yo'lakka o'tiladi.</p>
          </section>
        )}
      </div>

      {/* --- Masshtab --- */}
      <div className="m3d-zoom">
        <button type="button" onClick={() => sceneRef.current?.zoomIn()} aria-label="Yaqinlashtirish" title="Yaqinlashtirish">+</button>
        <button type="button" onClick={() => sceneRef.current?.zoomOut()} aria-label="Uzoqlashtirish" title="Uzoqlashtirish">−</button>
        <button type="button" onClick={() => { setFocus("A"); sceneRef.current?.reset(); }} aria-label="Boshlang'ich ko'rinish" title="Boshlang'ich ko'rinish">⟲</button>
        {!coarse && (
          <button
            type="button"
            className={`m3d-fpv-btn${fpv.active ? " active" : ""}`}
            onClick={toggleFpv}
            aria-pressed={fpv.active}
            title="Birinchi shaxs ko'rinishi (F): tanlangan joy oldidan yoki Nazoratdan"
          >
            FPV
          </button>
        )}
      </div>

      {/* --- Pastki tasma --- */}
      <div className="m3d-bottom">
        <div className="m3d-tabs" role="tablist">
          {([
            ["workers", `Xodimlar ro'yxati · ${workers.length}`],
            ["pallets", `Palletlar · ${palletChips.length}`],
            ["stats", "Statistika"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={tab === key ? "active" : ""}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="m3d-strip">
          {tab === "workers" && !canSeeWorkers && (
            <div className="m3d-muted">Xodimlar joyini ko'rish uchun «topshiriq taqsimlash» vakolati kerak.</div>
          )}
          {tab === "workers" && canSeeWorkers && positions.isError && (
            <div className="m3d-muted">Xodimlar joyi o'qilmadi.</div>
          )}
          {tab === "workers" && canSeeWorkers && !positions.isError && workers.length === 0 && (
            <div className="m3d-muted">{positions.isLoading ? "Yuklanmoqda…" : "Faol ijrochi yo'q"}</div>
          )}
          {tab === "workers" && workers.map((worker) => (
            <button
              key={worker.position.user_id}
              type="button"
              className={`m3d-chip${selection?.type === "worker" && selection.id === worker.position.user_id ? " active" : ""}`}
              onClick={() => selectWorker(worker.position.user_id)}
            >
              <span className={`m3d-status m3d-status-${worker.status}`}>
                {worker.status === "live" ? "Faol" : worker.status === "idle" ? "Kutmoqda" : worker.status === "stale" ? "Eskirgan" : "Amalsiz"}
              </span>
              <span className="m3d-chip-name">
                {worker.position.kind === "forklift" ? "KARA · " : ""}
                {worker.name}
              </span>
              <span className="m3d-chip-sub">
                {worker.position.current ? statusLabel(worker.position.current.task_type) : "bo'sh"}
                {" · "}
                {worker.marker ? (worker.minutes != null ? minutesLabel(worker.minutes) : "—") : "joy noma'lum"}
              </span>
              <span className="m3d-chip-go" aria-hidden="true">↗</span>
            </button>
          ))}

          {tab === "pallets" && palletChips.length === 0 && (
            <div className="m3d-muted">{orderLps.isLoading ? "Yuklanmoqda…" : "Hududlarda pallet yo'q"}</div>
          )}
          {tab === "pallets" && palletChips.map((pallet) => (
            <button
              key={pallet.marker.key}
              type="button"
              className={`m3d-chip${selection?.type === "pallet" && selection.key === pallet.marker.key ? " active" : ""}`}
              onClick={() => selectPallet(pallet.marker.key)}
            >
              <span className={`m3d-status m3d-status-${pallet.marker.tone}`}>
                {pallet.lp ? statusLabel(pallet.lp.status) : "LPsiz"}
              </span>
              <span className="m3d-chip-name">{pallet.lp?.lp_code ?? pallet.cell.code}</span>
              <span className="m3d-chip-sub">
                {pallet.order ? `${pallet.order.order_number} · ${pallet.order.customer_name ?? "—"}` : pallet.cell.code}
              </span>
              <span className="m3d-chip-go" aria-hidden="true">↗</span>
            </button>
          ))}

          {tab === "stats" && (
            <div className="m3d-stats">
              <Stat label="A katak" value={stats.cells.toLocaleString("ru-RU")} />
              <Stat label="Band" value={`${stats.cells ? Math.round((stats.occupied / stats.cells) * 100) : 0}%`} />
              <Stat label="Bloklangan" value={String(stats.blocked)} />
              <Stat label="O'tish stellaji" value={String(stats.passBays)} />
              <Stat label="Hudud palleti" value={String(stats.zonePallets)} />
              <Stat label="Buyurtma palleti" value={String(stats.orderPallets)} />
              <Stat label="Faol xodim" value={`${stats.live} / ${workers.length}`} />
              <Stat label="Kara" value={String(stats.forklifts)} />
              {stats.unplaced > 0 && <Stat label="Xaritada yo'q" value={String(stats.unplaced)} />}
              <div className="m3d-legend">
                <span><i className="sw sw-full" />Band</span>
                <span><i className="sw sw-partial" />Javon</span>
                <span><i className="sw sw-blocked" />Blok</span>
                <span><i className="sw sw-accent" />Nazorat / kara / marshrut</span>
                <span><i className="sw sw-pass" />O'tish</span>
                <span className="m3d-muted">Belgi {STALE_MINUTES} daqiqadan eski — xira</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedWorker && !selectedWorker.marker && (
        <div className="m3d-banner m3d-brackets m3d-banner-low">
          {selectedWorker.name}: oxirgi {12} soatda joyli amal yo'q — xaritada ko'rsatilmadi. Oxirgi amal: {stepLabel(selectedWorker.position.last_action)}.
        </div>
      )}
      {routing.length != null && (
        <div className="m3d-route-chip m3d-brackets">
          <span className="m3d-kicker">{selectedWorker ? "Qolgan yo'l" : "Nazoratdan"}</span>
          <b>{metres(routing.length)}</b>
        </div>
      )}
    </div>
  );
}

/** B va C binolaridagi saqlash palleti — «kimning zakasi» ro'yxatiga kirmaydi. */
function inBuilding(layout: Layout, zoneId: string | undefined): boolean {
  return layout.config.zones.find((zone) => zone.id === zoneId)?.tone === "building";
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="m3d-stat">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

/** Kirish nuqtasidan katakka qaragan yaw (0 — shimolga, -Z). */
function faceYaw(from: Point, to: Point): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  if (Math.abs(dx) < 1 && Math.abs(dz) < 1) return 0;
  return Math.atan2(-dx, -dz);
}
